import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/Auth";
import { toast } from "react-toastify";
import { RotatingLines } from "react-loader-spinner";
import { showApiErrors } from "../../utils/helpers";
import TableLoader from "../../components/Loading/TableLoader";
import SortableTableHeader from "../../components/Table/SortableTableHeader";
import Swal from "sweetalert2";
import {
  Calendar,
  Users,
  Plus,
  Search,
  Filter,
  X,
  Edit2,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  UserPlus,
} from "lucide-react";
import { Link } from "react-router-dom";

const StaffSchedule = () => {
  const { requestWithAuth } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkCount, setBulkCount] = useState(1);
  const [dateFilter, setDateFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sessionFilter, setSessionFilter] = useState("");
  const [gettingSchedules, setGettingSchedules] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("")
  const [staffMembers, setStaffMembers] = useState([]);
  const [addingSchedule, setAddingSchedule] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");

  const fetchUsers = async () => {
    try {
      const data = await requestWithAuth(
        `/users/accounts/?paginated=false&status=Active&staff_role=dispenser`,
        "GET"
      );
      setStaffMembers(data);
    } catch (error) {
      console.error("Error fetching users data:", error);
    }
  };

  const getBranches = async () => {
    try {
      const data = await requestWithAuth(
        `/company/branches/?paginated=false&status=Active&branch_type=retail`,
        "GET"
      );
      setBranches(data);
    } catch (error) { }
  };

  const getSchedules = async (search, date, session, branchSearch, page = 1, current_page_size = 10, sort_by = null, sort_order = "desc") => {
    try {
      setGettingSchedules(true);
      const sortParam = sort_by ? `&sort_by=${sort_by}&sort_order=${sort_order}` : "";
      const response = await requestWithAuth(
        `/users/staff-schedule?search=${search}&date=${date}&session=${session}&branch=${branchSearch}&page=${page}&pageSize=${current_page_size}${sortParam}`,
        "GET"
      );
      const data = response?.data;
      setSchedules(data?.results || []);
      setNextPage(data?.next);
      setPrevPage(data?.previous);
      setTotalPages(Math.ceil(data?.count / current_page_size));
      setGettingSchedules(false);
    } catch (error) {
      toast.error("Failed to get schedules");
      setGettingSchedules(false);
    }
  };

  const handleSort = (key) => {
    setCurrentPage(1);
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("desc");
    }
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Form state
  const [formData, setFormData] = useState({
    user: "",
    date: "",
    session: "",
    branch: "",
    is_active: true,
  });

  const sessions = ["morning", "evening"];

  useEffect(() => {
    fetchUsers();
    getBranches();
  }, []);

  // Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [dateFilter, searchTerm, sessionFilter, branchFilter, sortBy, sortOrder]);

  // Fetch schedules when pagination or filters change
  useEffect(() => {
    getSchedules(searchTerm, dateFilter, sessionFilter, branchFilter, currentPage, pageSize, sortBy, sortOrder);
  }, [dateFilter, searchTerm, sessionFilter, branchFilter, currentPage, pageSize, sortBy, sortOrder]);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-500 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  const clearAllFilters = () => {
    setDateFilter("");
    setSearchTerm("");
    setSessionFilter("");
  };

  const resetForm = () => {
    setFormData({
      user: "",
      date: "",
      session: "",
      branch: "",
      is_active: true,
    });
    setEditingSchedule(null);
    setBulkMode(false);
    setBulkCount(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAddingSchedule(true);
    if (bulkMode) {
      for (let i = 0; i < bulkCount; i++) {
        const newDate = new Date(formData.date);
        newDate.setDate(newDate.getDate() + i);

        const newFormData = {
          ...formData,
          date: newDate.toISOString().split("T")[0],
        };

        try {
          await requestWithAuth(`/users/staff-schedule/`, "POST", newFormData);
        } catch (error) {
          showApiErrors(error);
        }
      }
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Schedules added successfully",
      }).then(() => {
        window.location.reload();
      });
    } else if (editingSchedule) {
      try {
        await requestWithAuth(
          `/users/staff-schedule/${editingSchedule.id}/`,
          "PUT",
          formData
        );
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Schedule updated successfully",
        }).then(() => {
          window.location.reload();
        });
      } catch (error) {
        showApiErrors(error);
      }
    } else {
      try {
        await requestWithAuth(`/users/staff-schedule/`, "POST", formData);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Schedule added successfully",
        }).then(() => {
          window.location.reload();
        });
      } catch (error) {
        showApiErrors(error);
      }
    }
    setAddingSchedule(false);
    setIsModalOpen(false);
    resetForm();
  };

  const handleEdit = (schedule) => {
    setFormData(schedule);
    setEditingSchedule(schedule);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await requestWithAuth(`/users/staff-schedule/${id}/`, "DELETE");
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "Schedule deleted successfully",
          }).then(() => {
            getSchedules(searchTerm, dateFilter, sessionFilter, branchFilter, currentPage, pageSize, sortBy, sortOrder);
          });
        } catch (error) {
          console.error("Error deleting schedule:", error);
          showApiErrors(error);
        }
      }
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Administration</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">
                  Staff Schedule
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Calendar size={32} className="text-blue-600" />
                Staff Schedule Manager
              </h1>
            </div>
            <div className="flex gap-3">
              <Link
                to="/administration/staff-schedule/bulk"
                className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
              >
                <Users size={18} />
                <span className="font-medium">Bulk Add</span>
              </Link>
              <button
                onClick={() => setIsModalOpen(true)}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
              >
                <Plus size={18} />
                <span className="font-medium">Add Schedule</span>
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter size={18} className="text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">Filters</h3>
            </div>
            {(searchTerm || dateFilter || sessionFilter) && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={16} />
                <span>Clear All</span>
              </button>
            )}
          </div>

          {/* Controls Bar */}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm text-gray-600">Show</span>
            <select
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              value={pageSize}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <span className="text-sm text-gray-600">entries</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search staff"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Date Filter */}
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />

            <select
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Branches</option>
              {branches
                .filter((branch) => branch.is_stores === false)
                .map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
            </select>

            {/* Status Filter */}
            <select
              value={sessionFilter}
              onChange={(e) => setSessionFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Session</option>
              <option value="morning">Morning</option>
              <option value="evening">Evening</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Staff Member
                  </th>
                  <SortableTableHeader
                    label="Date"
                    fieldKey="date"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    label="Session"
                    fieldKey="session"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Branch
                  </th>
                  <SortableTableHeader
                    label="Status"
                    fieldKey="is_active"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              {!gettingSchedules && schedules.length > 0 && (
                <tbody className="divide-y divide-gray-200">
                  {schedules.map((schedule) => (
                    <tr
                      key={schedule.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                            {schedule.user_name
                              ?.split(" ")
                              .map((n) => n[0])
                              .join("")
                              .toUpperCase()}
                          </div>
                          <span className="font-medium text-gray-900">
                            {schedule.user_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {(() => {
                          if (!schedule.date) return "";
                          const [year, month, day] = schedule.date.split("-");
                          return new Date(year, month - 1, day).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          });
                        })()}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 capitalize">
                          <Clock size={12} />
                          {schedule.session}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          <Building2 size={12} />
                          {schedule.branch_name}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${schedule.is_active
                            ? "bg-green-50 text-green-700"
                            : "bg-red-50 text-red-700"
                            }`}
                        >
                          {schedule.is_active ? (
                            <CheckCircle size={12} />
                          ) : (
                            <XCircle size={12} />
                          )}
                          {schedule.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleEdit(schedule)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(schedule.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
            {gettingSchedules && <TableLoader />}
            {!gettingSchedules && schedules.length === 0 && (
              <div className="text-center py-12">
                <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No schedules found
                </h3>
                <p className="text-gray-500 mb-4">
                  Get started by creating your first schedule
                </p>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus size={18} />
                  Add Schedule
                </button>
              </div>
            )}
          </div>

          {/* Pagination */}
          {schedules.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-all ${currentPage > 1
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">
                  {editingSchedule
                    ? "Edit Schedule"
                    : bulkMode
                      ? "Bulk Add Schedules"
                      : "Add New Schedule"}
                </h2>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                  className="text-white hover:bg-white/20 p-1 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <UserPlus size={16} className="text-gray-500" />
                  Staff Member
                  <span className="text-red-500">*</span>
                </label>
                <select
                  name="user"
                  value={formData.user}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Staff</option>
                  {staffMembers.map((staff) => (
                    <option key={staff?.id} value={staff?.id}>
                      {staff?.full_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <Calendar size={16} className="text-gray-500" />
                  Date
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {bulkMode && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Number of Days
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={bulkCount}
                    onChange={(e) => setBulkCount(parseInt(e.target.value))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1.5">
                    Creates consecutive schedules starting from the selected
                    date
                  </p>
                </div>
              )}

              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <Clock size={16} className="text-gray-500" />
                  Session
                  <span className="text-red-500">*</span>
                </label>
                <select
                  name="session"
                  value={formData.session}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 capitalize"
                >
                  <option value="">Select Session</option>
                  {sessions.map((session) => (
                    <option
                      key={session}
                      value={session}
                      className="capitalize"
                    >
                      {session}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <Building2 size={16} className="text-gray-500" />
                  Branch
                  <span className="text-red-500">*</span>
                </label>
                <select
                  name="branch"
                  value={formData.branch}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Branch</option>
                  {branches
                    .filter((branch) => branch.is_stores === false)
                    .map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="text-sm text-gray-700">Active Schedule</label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                  className="flex-1 py-2.5 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={addingSchedule}
                  className={`flex-1 py-2.5 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium flex items-center justify-center gap-2 ${addingSchedule ? "opacity-75 cursor-not-allowed" : ""
                    }`}
                >
                  {addingSchedule ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {editingSchedule ? "Updating..." : "Adding..."}
                    </>
                  ) : editingSchedule ? (
                    "Update Schedule"
                  ) : bulkMode ? (
                    `Add ${bulkCount} Schedule${bulkCount > 1 ? "s" : ""}`
                  ) : (
                    "Add Schedule"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffSchedule;
